import { type KeyAction } from '@solinkcorp/solink-components';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseServiceDisplayDebuggerReturn {
  isDebuggerEnabled: boolean;
  isMinimized: boolean;
  toggleDebugger: () => void;
  toggleMinimize: () => void;
  debuggerActions: KeyAction[];
}

export const useServiceDisplayDebugger = (): UseServiceDisplayDebuggerReturn => {
  const [isDebuggerEnabled, setIsDebuggerEnabled] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const comboSequenceRef = useRef<string[]>([]);

  const toggleDebugger = useCallback(() => {
    setIsDebuggerEnabled((prev) => !prev);
  }, []);

  const toggleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  // The Hadouken hotkey for displaying the debugger and 'A' for minimize
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.key) return;
      const key = event.key.toLowerCase();

      // Handle 'A' key for minimize (only when debugger is open)
      if (key === 'a' && isDebuggerEnabled) {
        // Check if this is part of the combo sequence
        const isComboSequence =
          comboSequenceRef.current.length >= 3 &&
          comboSequenceRef.current[0] === 'arrowdown' &&
          comboSequenceRef.current[1] === 'arrowright' &&
          comboSequenceRef.current[2] === 'arrowright';

        if (!isComboSequence) {
          // This is a standalone 'A' press for minimize
          event.preventDefault();
          toggleMinimize();
          return;
        }
      }

      // Only track arrow keys and 'a' for combo
      if (['arrowdown', 'arrowright', 'a'].includes(key)) {
        // Add key to sequence
        comboSequenceRef.current = [...comboSequenceRef.current, key];

        // Keep only last 4 keys
        if (comboSequenceRef.current.length > 4) {
          comboSequenceRef.current.shift();
        }

        // Check for the exact combo: down, right, right, a
        if (
          comboSequenceRef.current.length === 4 &&
          comboSequenceRef.current[0] === 'arrowdown' &&
          comboSequenceRef.current[1] === 'arrowright' &&
          comboSequenceRef.current[2] === 'arrowright' &&
          comboSequenceRef.current[3] === 'a'
        ) {
          toggleDebugger();
          comboSequenceRef.current = [];
        }
      } else {
        comboSequenceRef.current = [];
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleDebugger, toggleMinimize, isDebuggerEnabled]);

  const debuggerActions: KeyAction[] = [];

  return {
    isDebuggerEnabled,
    isMinimized,
    toggleDebugger,
    toggleMinimize,
    debuggerActions,
  };
};

export default useServiceDisplayDebugger;
